import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {Pesata} from '../_models/Pesata';
import {HubConnection} from '@microsoft/signalr';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';
import {OrganizzazioniService} from './organizzazioni.service';
import {SaveMessage} from '../_helpers/save-message';
import {environment} from '../../environments/environment';
import {Risorsa} from "../_models/Risorsa";
import {TipoRisorsa} from '../_models/generated/tipo-risorsa'
import {RisorseService} from "./risorse.service";

const emptyBilancia: Risorsa = {
  reparto: '',
  organizzazioneId: 0,
  formato: '',
  indirizzoIp: '',
  id: 0,
  codice: '',
  descrizione: '',
  isActive: true,
  continuousLaunch: false,
  continuousSend: false,
  condition: 0,
  avanzaMode: 0,
  tipoRisorsa: TipoRisorsa.TUTTO
};
const cachePrefix = 'archiconnect-v2-';

@Injectable({providedIn: 'root'})
export class BilanceService {
  public searchBilanciaCode: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public searchBilancia: BehaviorSubject<Risorsa> = new BehaviorSubject<Risorsa>(null);
  public lastPesataCreated: BehaviorSubject<Pesata> = new BehaviorSubject<Pesata>(null);
  public lastPesataUpdated: BehaviorSubject<Pesata> = new BehaviorSubject<Pesata>(null);
  public lastPesataDeleted: BehaviorSubject<Pesata> = new BehaviorSubject<Pesata>(null);
  public hubState: BehaviorSubject<number> = new BehaviorSubject<number>(HubState.Connecting);
  public activeCodArt: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private hubConnection: HubConnection;

  constructor(
    private organizzazioniService: OrganizzazioniService,
    private saveMessage: SaveMessage,
    private risorseService: RisorseService) {
    const bilanciaCodeJson = localStorage.getItem(cachePrefix + 'bilanciacode');
    if (bilanciaCodeJson !== undefined && bilanciaCodeJson !== 'undefined') {
      const bilanciaCode = bilanciaCodeJson || '';
      this.searchBilanciaCode.next(bilanciaCode);
    }

    const bilancia = JSON.parse(localStorage.getItem(cachePrefix + 'bilancia')) || emptyBilancia;
    this.searchBilancia.next(bilancia);

    // Aggiornamento globale: quando SignalR notifica una risorsa aggiornata, se è la bilancia
    // in cache la riallinea ovunque (localStorage + searchBilancia → badge in spedizione-manuale).
    // Vivendo nel service (singleton root), funziona a prescindere dalla schermata aperta.
    this.risorseService.lastRisorsaUpdated.subscribe((risorsa: Risorsa) => {
      if (risorsa && this.searchBilancia.value && this.searchBilancia.value.id === risorsa.id) {
        this.setBilancia(risorsa);
      }
    });
  }

  setBilanciaCode(bilanciaCode: string) {
    if (bilanciaCode !== undefined) {
      this.searchBilanciaCode.next(bilanciaCode);
      localStorage.setItem(cachePrefix + 'bilanciacode', bilanciaCode);
    }
  }

  setBilancia(bilancia: Risorsa) {
    if (bilancia !== undefined) {
      this.searchBilancia.next(bilancia);
      localStorage.setItem(cachePrefix + 'bilancia', JSON.stringify(bilancia));
    }
  }

  resetBilanciaCode() {
    this.searchBilanciaCode.next('');
    localStorage.setItem(cachePrefix + 'bilanciacode', '');
  }

  resetBilancia() {
    this.searchBilancia.next(emptyBilancia);
    localStorage.setItem(cachePrefix + 'bilancia', JSON.stringify(emptyBilancia));
    this.resetBilanciaCode();
  }

  public startConnection() {
    this.hubConnection = buildHubConnection(`${environment.pesataHubUrl}hubs/pesata`, this.hubState);

    this.hubConnection.on('SendCreated', (pesata: Pesata) => {
      if (pesata.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.messageInfo('Nuova pesata creata.');
        this.lastPesataCreated.next(pesata);
      }
    });

    this.hubConnection.on('SendUpdated', (pesata: Pesata) => {
      if (pesata.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.messageInfo('Pesata aggiornata.');
        this.lastPesataUpdated.next(pesata);
      }
    });

    this.hubConnection.on('SendDeleted', (pesata: Pesata) => {
      if (pesata.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.messageInfo('Pesata eliminata.');
        this.lastPesataDeleted.next(pesata);
      }
    });

    return startHubConnection(this.hubConnection, this.hubState);
  }

  public stopConnection() {
    void stopHubConnection(this.hubConnection);
  }
}
